import { useState, useEffect } from 'react';
import { Search, Tag, X } from 'lucide-react';
import { PaletteRecord } from '../lib/database';

interface PaletteSearchPanelProps {
  onFilteredPalettesChange: (palettes: PaletteRecord[]) => void;
  allPalettes: PaletteRecord[];
}

export function PaletteSearchPanel({ onFilteredPalettesChange, allPalettes }: PaletteSearchPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  // Extract all unique tags from palettes
  useEffect(() => {
    const tagSet = new Set<string>();
    allPalettes.forEach((palette) => {
      palette.tags?.forEach((tag) => tagSet.add(tag));
    });
    // Defer state update to avoid synchronous setState in effect
    setTimeout(() => {
      setAvailableTags(Array.from(tagSet).sort());
    }, 0);
  }, [allPalettes]);

  // Filter palettes based on search query and selected tags
  useEffect(() => {
    let filtered = allPalettes;

    // Apply text search
    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (palette) =>
          palette.name.toLowerCase().includes(lowerQuery) ||
          palette.tags?.some((tag) => tag.toLowerCase().includes(lowerQuery))
      );
    }

    // Apply tag filters
    if (selectedTags.length > 0) {
      filtered = filtered.filter((palette) =>
        selectedTags.every((selectedTag) => palette.tags?.includes(selectedTag))
      );
    }

    onFilteredPalettesChange(filtered);
  }, [searchQuery, selectedTags, allPalettes, onFilteredPalettesChange]);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedTags([]);
  };

  const hasActiveFilters = searchQuery.trim() !== '' || selectedTags.length > 0;

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Search className="w-4 h-4" />
          Search & Filter
        </h3>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            Clear
          </button>
        )}
      </div>

      {/* Search Input */}
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name or tag..."
          className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
        />
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
      </div>

      {/* Tag Filter Chips */}
      {availableTags.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center gap-1">
            <Tag className="w-3 h-3" />
            Filter by tags:
          </p>
          <div className="flex flex-wrap gap-2">
            {availableTags.map((tag) => {
              const isSelected = selectedTags.includes(tag);
              const count = allPalettes.filter((p) => p.tags?.includes(tag)).length;
              
              return (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    isSelected
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <Tag className="w-3 h-3" />
                  {tag}
                  <span className="text-[10px] opacity-75">({count})</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Results Count */}
      <div className="text-xs text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-200 dark:border-gray-700">
        Showing {onFilteredPalettesChange.length !== undefined ? 'filtered' : 'all'} palettes
        {selectedTags.length > 0 && ` with tags: ${selectedTags.join(', ')}`}
      </div>
    </div>
  );
}
